import { FC, useCallback } from 'react'
import { Badge } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import dayjs, { Dayjs } from 'dayjs'
import { useProtocolsList } from '@services/protocols/protocols.hook'
import { protocolsDate, leaseDate, getProtocolFetchRange } from '@services/date/date.models'
import { useAtom } from 'jotai'

import 'dayjs/locale/pl'

const Day: FC<PickersDayProps<Dayjs> & { protocols?: any[] }> = ({
  protocols = [],
  day,
  ...props
}) => {
  const hasEvents = !!protocols
    .filter(({ leaseStartDate, effectiveLeaseEnd }) => !!leaseStartDate || !!effectiveLeaseEnd)
    .filter(
      ({ leaseStartDate, effectiveLeaseEnd }) =>
        day.isSame(leaseStartDate, 'day') || day.isSame(effectiveLeaseEnd, 'day')
    ).length

  if (props.outsideCurrentMonth || !hasEvents) {
    return <PickersDay {...props} day={day} />
  }

  return (
    <Badge color='error' key={day.toString()} overlap='circular' badgeContent='' variant='dot'>
      <PickersDay {...props} day={day} />
    </Badge>
  )
}

function Calendar() {
  const navigate = useNavigate()
  const { protocolsList } = useProtocolsList()
  const [date, setDate] = useAtom(protocolsDate)
  const [, setLeaseDate] = useAtom(leaseDate)

  const onMonthChange = useCallback(
    (nextDate: Dayjs) => {
      setLeaseDate(getProtocolFetchRange(nextDate))
      setDate(nextDate.toISOString())
    },
    [setLeaseDate, setDate]
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='pl'>
      <DateCalendar
        onYearChange={onMonthChange}
        onMonthChange={onMonthChange}
        onChange={(nextDate) => {
          if (nextDate) {
            setLeaseDate(getProtocolFetchRange(nextDate))
            setDate(nextDate.toISOString())
            navigate('/')
          }
        }}
        value={dayjs(date)}
        slots={{ day: Day }}
        slotProps={{
          day: {
            protocols: protocolsList
          } as any
        }}
      />
    </LocalizationProvider>
  )
}

export default Calendar
